import { inject, Injectable } from '@angular/core';
import { IField, IMask, ISuggestion } from '@web/dtos';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ESubscriptionDataTypes } from '@web/dtos';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionFormService {
  private readonly formsByMaskId: Map<string, FormGroup> = new Map<
    string,
    FormGroup
  >();
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);

  public resetForm() {
    this.formsByMaskId.clear();
  }

  public getOrCreateForm(mask: IMask): FormGroup {
    const id: string = mask.id;
    const existingFormGroup: FormGroup | undefined = this.formsByMaskId.get(id);
    if (existingFormGroup) return existingFormGroup;
    const createdFormGroup: FormGroup = this.createFormForMask(mask);
    this.formsByMaskId.set(id, createdFormGroup);
    return createdFormGroup;
  }

  private createFormForMask(mask: IMask): FormGroup {
    const group: FormGroup = this.formBuilder.group({});

    mask.fields.forEach((field: IField) => {
      const validators = field.required ? [Validators.required] : [];

      switch (field.datatype) {
        case ESubscriptionDataTypes.String:
        case ESubscriptionDataTypes.Guid:
        case ESubscriptionDataTypes.EMail:
          group.addControl(
            field.internalName,
            this.formBuilder.nonNullable.control<string>(
              '',
              validators.concat(
                field.datatype === ESubscriptionDataTypes.EMail
                  ? [Validators.email]
                  : []
              )
            )
          );
          break;

        case ESubscriptionDataTypes.Boolean:
          group.addControl(
            field.internalName,
            this.formBuilder.nonNullable.control<boolean>(
              false,
              validators.concat(field.required ? [Validators.requiredTrue] : [])
            )
          );
          break;

        case ESubscriptionDataTypes.Date:
          group.addControl(
            field.internalName,
            this.formBuilder.control<Date | null>(null, validators)
          );
          break;

        case ESubscriptionDataTypes.SelectionField:
          group.addControl(
            field.internalName,
            this.formBuilder.control<number | null>(null, validators)
          );

          field.dropdownOptions = field.suggestions?.map(
            (sug: ISuggestion, index: number) => ({
              value: index,
              name: sug.value,
            })
          );
          break;

        case ESubscriptionDataTypes.Integer:
        case ESubscriptionDataTypes.Price:
        case ESubscriptionDataTypes.PriceLevel:
          group.addControl(
            field.internalName,
            this.formBuilder.nonNullable.control<number>(
              0,
              validators.concat([Validators.pattern('^-?\\d+$')])
            )
          );
          break;

        default:
          group.addControl(
            field.internalName,
            this.formBuilder.nonNullable.control<string>('', validators)
          );
          break;
      }
    });

    return group;
  }
}
